import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';

const verifyCodeSchema = z.object({
  code: z
    .string()
    .min(1, 'Please enter the verification code')
    .regex(/^\d+$/, 'Code must contain only numbers')
    .refine((val) => val.length >= 4, 'Code must be at least 4 digits'),
});

type VerifyCodeFormData = z.infer<typeof verifyCodeSchema>;

const VerifyResetCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const email = location?.state?.email as string | undefined;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyCodeFormData>({
    resolver: zodResolver(verifyCodeSchema),
    defaultValues: {
      code: '',
    },
  });

  const onSubmit = async (data: VerifyCodeFormData) => {
    if (!email) {
      toast.error('Missing email context');
      navigate('/forgot-password');
      return;
    }

    const { data: verifyData, error } = await supabase.auth.verifyOtp({
      email,
      token: data.code,
      type: 'recovery',
    } as any);

    if (error) {
      toast.error(mapSupabaseErrorMessage(error.message));
      return;
    }

    toast.success('Code verified');
    navigate('/reset-password/new', { state: { email } });
  };

  return (
    <div className="eg-auth-page auth-page">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div className="eg-auth-card">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light tracking-wide" style={{ color: 'var(--color-auth-text)' }}>Verify Code</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <input
                type="text"
                placeholder="Enter your code"
                {...register('code', {
                  onChange: (e) => {
                    // Filter to only allow numeric input
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  },
                })}
                className={`eg-auth-input text-center tracking-widest text-lg ${errors.code ? 'input-error' : ''}`}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-400 text-center">{errors.code.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="eg-auth-btn"
            >
              {isSubmitting ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          <div className="mt-3 text-base text-center" style={{ color: 'var(--color-auth-text-secondary)' }}>
            <span>
              Remember password?{' '}
              <Link to="/login" className="inline-block py-1 underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded" style={{ color: 'var(--color-auth-link)' }}>
                Login
              </Link>
            </span>
          </div>
          <div className="mt-4 text-xs text-center" style={{ color: 'var(--color-auth-text-secondary)' }}>
            <p>If you have an Engramiq account you will receive an email with password reset instructions. If you do not receive an email try signing up or contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a> for assistance</p>
          </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default VerifyResetCodePage;
